import { useEffect, useMemo, useState } from 'react';

import { NARRATIVE_WIDGETS_IN_REPORT_COMPONENT_KEYS } from 'constants/componentKeys/narrativeWidgetsInReport.componentKeys';
import { onWidgetReady } from 'services/eventBus/narrativeWidgetsEventBus';

import { IComponentKey } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { Widget } from 'components/Dashboards/Reporting/reporting.types';

interface Props {
    widgets?: Widget[];
}

/**
 * Hook that tracks when all narrative widgets in a report are ready.
 * Subscribes to widget-ready events via event bus.
 */
export const useNarrativeWidgetsReady = ({ widgets }: Props) => {
    const [readyWidgetIds, setReadyWidgetIds] = useState<Set<string>>(new Set());

    // Get IDs of widgets that need tracking
    const trackedWidgetIds = useMemo(() => {
        if (!widgets) return [];
        return widgets
            .filter((widget) =>
                NARRATIVE_WIDGETS_IN_REPORT_COMPONENT_KEYS.includes(widget.componentKey as IComponentKey),
            )
            .map((widget) => String(widget.id));
    }, [widgets]);

    // Subscribe to widget-ready events
    useEffect(() => {
        // Reset ready IDs on tracked widgets change
        setReadyWidgetIds(new Set());

        const handleWidgetReady = ({ widgetId }: { widgetId: string }) => {
            if (trackedWidgetIds.includes(widgetId)) {
                setReadyWidgetIds((prev) => {
                    if (prev.has(widgetId)) return prev;
                    return new Set(prev).add(widgetId);
                });
            }
        };

        const unsubscribe = onWidgetReady(handleWidgetReady);

        return unsubscribe;
    }, [trackedWidgetIds]);

    // Compute readiness
    const isAllNarrativeWidgetsReady = trackedWidgetIds.length === 0 || readyWidgetIds.size === trackedWidgetIds.length;

    return {
        isAllNarrativeWidgetsReady,
    };
};
